import { isDefined } from "@clipboard-health/util-ts";

import {
  INTERVIEW_TYPE,
  InterviewStatus,
} from "../../WorkerShifts/api/useFetchPaginatedInterviews";
import { getHighestRankingQualification } from "../api/useHiringQualificationRanking";
import { checkIsProfileCompletedForPlacement } from "../components/PlacementApplyButton/utils/checkIsProfileCompletedForPlacement";
import { type InterviewOnJobCardsConfig } from "../components/PlacementCard/types";
import { type PlacementData } from "../types/placement";

interface UseInterviewsOnJobCardsParams {
  placement: Omit<
    PlacementData,
    "score" | "scoreBreakdown" | "baseRate" | "totalRate" | "totalRateBreakdown"
  >;
  isRestricted: boolean;
  interviewOnJobCardsConfig?: InterviewOnJobCardsConfig;
}

export function useInterviewsOnJobCards(params: UseInterviewsOnJobCardsParams) {
  const { placement, isRestricted, interviewOnJobCardsConfig } = params;

  if (!interviewOnJobCardsConfig) {
    return { showInterviewsOnJobCards: false };
  }

  const { isProfileCompleted, autoApprovedQualifications, isJobInterviewsEnabled } =
    interviewOnJobCardsConfig;

  const highestRankingSharedQualification = getHighestRankingQualification(
    placement.workerTypes ?? []
  );

  const isProfileComplete = checkIsProfileCompletedForPlacement(
    isProfileCompleted,
    highestRankingSharedQualification,
    placement.workerTypes,
    autoApprovedQualifications
  );

  const latestInterview = placement.interviews
    .toSorted(
      (a, b) =>
        new Date(a.attributes.createdAt).getTime() - new Date(b.attributes.createdAt).getTime()
    )
    .at(-1);
  const hasBookedInterview =
    latestInterview?.attributes.status === InterviewStatus.BOOKED ||
    latestInterview?.attributes.status === InterviewStatus.PENDING;

  const hasMissingDocuments = placement.workerRequirements?.some(
    (requirement) => !requirement.isValid
  );

  const hasMissingCredentials = placement.credentialRequirements?.some(
    (requirement) => !requirement.isValid
  );

  const canScheduleInterviews = placement.interviewSettings?.scheduleInterviews ?? false;

  const isInPersonInterview =
    placement.interviewSettings?.selfServeInterviewBookingType === INTERVIEW_TYPE.IN_PERSON;

  const isProspectWorkplace = placement.workplace.isProspect ?? false;

  // We only want to show interviews on job cards for ONLY in-person interviews
  // when a worker is eligible for the placement
  const showInterviewsOnJobCards =
    isJobInterviewsEnabled &&
    isDefined(placement.workplace.interviewDays) &&
    placement.workplace.interviewDays.length > 0 &&
    canScheduleInterviews &&
    !hasBookedInterview &&
    !hasMissingDocuments &&
    !hasMissingCredentials &&
    !placement.isEasyScheduling &&
    !placement.allowRequestInterview &&
    placement.isLicenseEligible &&
    isInPersonInterview &&
    !isProspectWorkplace &&
    !isRestricted &&
    isProfileComplete;

  return {
    showInterviewsOnJobCards,
  };
}
